"use strict";
/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.subErrorsAt = exports.errorAt = exports.errorsAt = exports.getControlPath = exports.extractAjv = exports.extractUiSchema = exports.extractSchema = exports.extractData = exports.coreReducer = exports.validate = void 0;
var tslib_1 = require("tslib");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var set_1 = tslib_1.__importDefault(require("lodash/fp/set"));
var get_1 = tslib_1.__importDefault(require("lodash/get"));
var filter_1 = tslib_1.__importDefault(require("lodash/filter"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var isFunction_1 = tslib_1.__importDefault(require("lodash/isFunction"));
var actions_1 = require("../actions");
var util_1 = require("../util");
var validate = function (validator, data) {
    if (validator === undefined) {
        return [];
    }
    var valid = validator(data);
    if (valid) {
        return [];
    }
    return validator.errors;
};
exports.validate = validate;
var initState = {
    data: {},
    schema: {},
    uischema: undefined,
    errors: [],
    validator: undefined,
    ajv: undefined,
    validationMode: 'ValidateAndShow',
};
var reuseAjvForSchema = function (ajv, schema) {
    if (schema.hasOwnProperty('id') || schema.hasOwnProperty('$id')) {
        ajv.removeSchema(schema);
    }
    return ajv;
};
var getOrCreateAjv = function (state, action) {
    if (action) {
        if (hasAjvOption(action.options)) {
            // options object with ajv
            return action.options.ajv;
        }
        else if (action.options !== undefined) {
            // it is not an option object => should be ajv itself => check for compile function
            if (isFunction_1.default(action.options.compile)) {
                return action.options;
            }
        }
    }
    if (state.ajv) {
        return (action === null || action === void 0 ? void 0 : action.schema)
            ? reuseAjvForSchema(state.ajv, action.schema)
            : state.ajv;
    }
    return util_1.createAjv();
};
var hasAjvOption = function (option) {
    if (option) {
        return option.ajv !== undefined;
    }
    return false;
};
var getValidationMode = function (state, action) {
    if (action && hasValidationModeOption(action.options)) {
        return action.options.validationMode;
    }
    return state.validationMode;
};
var hasValidationModeOption = function (option) {
    if (option) {
        return option.validationMode !== undefined;
    }
    return false;
};
// tslint:disable-next-line: cyclomatic-complexity
var coreReducer = function (state, action) {
    if (state === void 0) { state = initState; }
    switch (action.type) {
        case actions_1.INIT: {
            var thisAjv = getOrCreateAjv(state, action);
            var validationMode = getValidationMode(state, action);
            var v = validationMode === 'NoValidation' ? undefined : thisAjv.compile(action.schema);
            var e = exports.validate(v, action.data);
            return tslib_1.__assign(tslib_1.__assign({}, state), { data: action.data, schema: action.schema, uischema: action.uischema, errors: e, validator: v, ajv: thisAjv, validationMode: validationMode });
        }
        case actions_1.UPDATE_CORE: {
            var thisAjv = getOrCreateAjv(state, action);
            var validationMode = getValidationMode(state, action);
            var validator = state.validator;
            var errors = state.errors;
            if (state.schema !== action.schema ||
                state.validationMode !== validationMode ||
                state.ajv !== thisAjv) {
                // revalidate only if necessary
                validator =
                    validationMode === 'NoValidation'
                        ? undefined
                        : thisAjv.compile(action.schema);
                errors = exports.validate(validator, action.data);
            }
            else if (state.data !== action.data) {
                errors = exports.validate(validator, action.data);
            }
            var stateChanged = state.data !== action.data ||
                state.schema !== action.schema ||
                state.uischema !== action.uischema ||
                state.ajv !== thisAjv ||
                state.errors !== errors ||
                state.validator !== validator ||
                state.validationMode !== validationMode;
            return stateChanged
                ? tslib_1.__assign(tslib_1.__assign({}, state), { data: action.data, schema: action.schema, uischema: action.uischema, ajv: thisAjv, errors: isEqual_1.default(errors, state.errors) ? state.errors : errors, validator: validator, validationMode: validationMode }) : state;
        }
        case actions_1.SET_AJV: {
            var currentAjv = action.ajv;
            var validator = state.validationMode === 'NoValidation' ? undefined : currentAjv.compile(state.schema);
            var errors = exports.validate(validator, state.data);
            return tslib_1.__assign(tslib_1.__assign({}, state), { validator: validator,
                errors: errors });
        }
        case actions_1.SET_SCHEMA: {
            var needsNewValidator = action.schema && state.ajv && state.validationMode !== 'NoValidation';
            var v = needsNewValidator
                ? reuseAjvForSchema(state.ajv, action.schema).compile(action.schema)
                : state.validator;
            var errors = exports.validate(v, state.data);
            return tslib_1.__assign(tslib_1.__assign({}, state), { validator: v, schema: action.schema, errors: errors });
        }
        case actions_1.SET_UISCHEMA: {
            return tslib_1.__assign(tslib_1.__assign({}, state), { uischema: action.uischema });
        }
        case actions_1.UPDATE_DATA: {
            if (action.path === undefined || action.path === null) {
                return state;
            }
            else if (action.path === '') {
                // empty path is ok
                var result = action.updater(cloneDeep_1.default(state.data));
                var errors = exports.validate(state.validator, result);
                return tslib_1.__assign(tslib_1.__assign({}, state), { data: result, errors: errors });
            }
            else {
                var oldData = get_1.default(state.data, action.path);
                var newData = action.updater(cloneDeep_1.default(oldData));
                var newState = set_1.default(action.path, newData, state.data === undefined ? {} : state.data);
                var errors = exports.validate(state.validator, newState);
                return tslib_1.__assign(tslib_1.__assign({}, state), { data: newState, errors: errors });
            }
        }
        case actions_1.UPDATE_ERRORS: {
            return tslib_1.__assign(tslib_1.__assign({}, state), { errors: action.errors });
        }
        case actions_1.SET_VALIDATION_MODE: {
            if (state.validationMode === action.validationMode) {
                return state;
            }
            if (action.validationMode === 'NoValidation') {
                var errors = exports.validate(undefined, state.data);
                return tslib_1.__assign(tslib_1.__assign({}, state), { errors: errors, validationMode: action.validationMode });
            }
            if (state.validationMode === 'NoValidation') {
                var validator = reuseAjvForSchema(state.ajv, state.schema).compile(state.schema);
                var errors = exports.validate(validator, state.data);
                return tslib_1.__assign(tslib_1.__assign({}, state), { validator: validator,
                    errors: errors, validationMode: action.validationMode });
            }
            return tslib_1.__assign(tslib_1.__assign({}, state), { validationMode: action.validationMode });
        }
        default:
            return state;
    }
};
exports.coreReducer = coreReducer;
var extractData = function (state) { return get_1.default(state, 'data'); };
exports.extractData = extractData;
var extractSchema = function (state) { return get_1.default(state, 'schema'); };
exports.extractSchema = extractSchema;
var extractUiSchema = function (state) { return get_1.default(state, 'uischema'); };
exports.extractUiSchema = extractUiSchema;
var extractAjv = function (state) { return get_1.default(state, 'ajv'); };
exports.extractAjv = extractAjv;
var getInvalidProperty = function (error) {
    switch (error.keyword) {
        case 'required':
        case 'dependencies':
            return error.params.missingProperty;
        case 'additionalProperties':
            return error.params.additionalProperty;
        default:
            return undefined;
    }
};
var getControlPath = function (error) {
    var dataPath = error.dataPath;
    // older AJV version
    if (dataPath) {
        return dataPath.replace(/\//g, '.').substr(1);
    }
    // dataPath was renamed to instancePath in AJV v8
    var controlPath = error.instancePath;
    // change '/' chars to '.'
    controlPath = controlPath.replace(/\//g, '.');
    var invalidProperty = getInvalidProperty(error);
    if (invalidProperty !== undefined && !controlPath.endsWith(invalidProperty)) {
        controlPath = controlPath + "." + invalidProperty;
    }
    // remove '.' chars at the beginning of paths
    controlPath = controlPath.replace(/^./, '');
    return controlPath;
};
exports.getControlPath = getControlPath;
var errorsAt = function (instancePath, schema, matchPath) { return function (errors) {
    // Get data paths of oneOf and anyOf errors to later determine whether an error occurred inside a subschema of oneOf or anyOf.
    var combinatorPaths = filter_1.default(errors, function (error) { return error.keyword === 'oneOf' || error.keyword === 'anyOf'; }).map(function (error) { return exports.getControlPath(error); });
    return filter_1.default(errors, function (error) {
        // Filter errors that match any keyword that we don't want to show in the UI
        if (filteredErrorKeywords.indexOf(error.keyword) !== -1) {
            return false;
        }
        var controlPath = exports.getControlPath(error);
        var result = matchPath(controlPath);
        // In anyOf and oneOf blocks with "primitive" (i.e. string, number etc.) or array subschemas,
        // we want to make sure that errors are only shown for the correct subschema.
        // Therefore, we compare the error's parent schema with the property's schema.
        // In the primitive case the error's data path is the same for all subschemas:
        // It directly points to the property defining the anyOf/oneOf.
        // The same holds true for errors on the array level (e.g. min item amount).
        // In contrast, this comparison must not be done for errors whose parent schema defines an object
        // because the parent schema can never match the property schema (e.g. for 'required' checks).
        var parentSchema = error.parentSchema;
        if (result && !isObjectSchema(parentSchema)
            && combinatorPaths.findIndex(function (p) { return instancePath.startsWith(p); }) !== -1) {
            result = result && isEqual_1.default(parentSchema, schema);
        }
        return result;
    });
}; };
exports.errorsAt = errorsAt;
/**
 * @returns true if the schema describes an object.
 */
var isObjectSchema = function (schema) {
    return (schema === null || schema === void 0 ? void 0 : schema.type) === 'object' || !!(schema === null || schema === void 0 ? void 0 : schema.properties);
};
/**
 * The error-type of an AJV error is defined by its `keyword` property.
 * Certain errors are filtered because they don't fit to any rendered control.
 * All of them have in common that we don't want to show them in the UI
 * because controls will show the actual reason why they don't match their correponding sub schema.
 * - additionalProperties: Indicates that a property is present that is not defined in the schema.
 *      Jsonforms only allows to edit defined properties. These errors occur if an oneOf doesn't match.
 * - allOf: Indicates that not all of the allOf definitions match as a whole.
 * - anyOf: Indicates that an anyOf definition itself is not valid because none of its subschemas matches.
 * - oneOf: Indicates that an oneOf definition itself is not valid because not exactly one of its subschemas matches.
 */
var filteredErrorKeywords = ['additionalProperties', 'allOf', 'anyOf', 'oneOf'];
var getErrorsAt = function (instancePath, schema, matchPath) { return function (state) {
    return exports.errorsAt(instancePath, schema, matchPath)(state.validationMode === 'ValidateAndHide' ? [] : state.errors);
}; };
var errorAt = function (instancePath, schema) {
    return getErrorsAt(instancePath, schema, function (path) { return path === instancePath; });
};
exports.errorAt = errorAt;
var subErrorsAt = function (instancePath, schema) {
    return getErrorsAt(instancePath, schema, function (path) { return path.startsWith(instancePath); });
};
exports.subErrorsAt = subErrorsAt;
//# sourceMappingURL=core.js.map